import React, { useEffect, useState } from 'react'
import './LandingPage.css'
import { Helmet } from 'react-helmet-async'
import BlogList from "./BlogList"
import { supabase } from '../supabaseClient'  // adjust the import path as needed
import Pricing from './Pricing'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import 'react-lazy-load-image-component/src/effects/blur.css'

// New import for animations
import { motion } from 'framer-motion'

// Custom hook for visibility animation
const useInView = () => {
  const [ref, setRef] = useState(null)
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    if (!ref) return
    
    const observer = new IntersectionObserver(([entry]) => {
      setIsVisible(entry.isIntersecting)
    }, { threshold: 0.1 })
    
    observer.observe(ref)
    
    return () => {
      if (ref) observer.unobserve(ref)
    }
  }, [ref])
  
  return [setRef, isVisible]
}

const LandingPage = () => {
  const [user, setUser] = useState(null)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Fetch the current user from Supabase on mount.
  useEffect(() => {
    async function fetchUser() {
      const {
        data: { user },
      } = await supabase.auth.getUser()
      setUser(user)
    }
    fetchUser()
  }, [])

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen)
  }

  // Testimonials data
  const testimonials = [
    {
      quote: "Thuis.AI helped me find my dream apartment in just 3 weeks, compared to 3 months of traditional searching!",
      name: "Sarah K.",
      title: "First-time Homebuyer",
      image: "/api/placeholder/60/60",
      rating: 5
    },
    {
      quote: "The AI recommendations were spot-on. I found a property that matched my preferences perfectly.",
      name: "Michael T.",
      title: "Real Estate Investor",
      image: "/api/placeholder/60/60",
      rating: 5
    },
    {
      quote: "The scheduling feature saved me so much time. No more back-and-forth emails with agents!",
      name: "Emma R.",
      title: "Young Professional",
      image: "/api/placeholder/60/60",
      rating: 4
    }
  ]

  // FAQ data
  const faqs = [
    {
      question: "How does the AI recommendation system work?",
      answer: "Our AI analyzes thousands of properties based on your preferences, budget, and priorities. It learns from your interactions to continuously improve recommendations, helping you find your ideal home faster."
    },
    {
      question: "Is my personal information secure?",
      answer: "Absolutely. We use bank-level encryption to protect all your data. We never share your personal information with third parties without your explicit consent."
    },
    {
      question: "How much does it cost to use Thuis.AI?",
      answer: "We offer different subscription plans based on your needs. You can start with our basic plan and upgrade as needed. Check out our pricing section for details."
    },
    {
      question: "Can I schedule multiple viewings at once?",
      answer: "Yes! Our smart scheduling system allows you to book multiple viewings, even on the same day, with optimal routing to save you time."
    }
  ]

  // Partner logos
  const partners = [
    { name: "RealEstate Co", logo: "/api/placeholder/140/60" },
    { name: "HomeFinance", logo: "/api/placeholder/140/60" },
    { name: "PropertyTech", logo: "/api/placeholder/140/60" },
    { name: "SmartHomes", logo: "/api/placeholder/140/60" },
  ]

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  }

  // Use our custom hook for scroll animations
  const [featuresRef, featuresVisible] = useInView()
  const [howItWorksRef, howItWorksVisible] = useInView()
  const [testimonialsRef, testimonialsVisible] = useInView()

  return (
    <div className="landing-container">
      <Helmet>
        <title>Thuis.AI | AI-Powered Real Estate Discovery</title>
        <meta charSet="UTF-8" />
        <meta
          name="description"
          content="Discover, schedule, and make offers on properties with AI-powered intelligence. Thuis.AI revolutionizes how you find your next home."
        />
        <meta
          name="keywords"
          content="real estate AI, property search, AI home discovery, property listings, smart real estate, schedule viewing, make offer"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="icon" href="/api/placeholder/32/32" type="image/png" />
        <meta
          property="og:title"
          content="Thuis.AI | AI-Powered Real Estate Discovery"
        />
        <meta
          property="og:description"
          content="Discover, schedule, and make offers on properties with AI-powered intelligence."
        />
        <meta property="og:image" content="/api/placeholder/1200/630" />
        <meta property="og:url" content="https://thuis.ai" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Thuis.AI | AI-Powered Real Estate Discovery"
        />
        <meta
          name="twitter:description"
          content="Discover, schedule, and make offers on properties with AI-powered intelligence."
        />
        <meta name="twitter:image" content="/api/placeholder/1200/630" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Thuis.AI",
            "url": "https://thuis.ai",
            "logo": "/api/placeholder/180/60",
            "description":
              "AI-powered real estate platform for discovering, scheduling viewings, and making offers on properties.",
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "USA",
            },
            "sameAs": [
              "https://twitter.com/thuisai",
              "https://facebook.com/thuisai",
              "https://instagram.com/thuisai",
            ],
          })}
        </script>
      </Helmet>

      <header className="header">
        <div className="container">
          <div className="header-content">
            <a href="#" className="logo">
              thuis<span className="logo-dot">.ai</span>
            </a>
            <ul className={`nav-links ${mobileMenuOpen ? 'nav-active' : ''}`}>
              <li>
                <a href="#features" className="nav-link" onClick={() => setMobileMenuOpen(false)}>
                  Features
                </a>
              </li>
              <li>
                <a href="#how-it-works" className="nav-link" onClick={() => setMobileMenuOpen(false)}>
                  How It Works
                </a>
              </li>
              <li>
                <a href="#pricing" className="nav-link" onClick={() => setMobileMenuOpen(false)}>
                  Pricing
                </a>
              </li>
              <li>
                <a href="#testimonials" className="nav-link" onClick={() => setMobileMenuOpen(false)}>
                  Testimonials
                </a>
              </li>
              <li>
                <a href="#blog" className="nav-link" onClick={() => setMobileMenuOpen(false)}>
                  Blog
                </a>
              </li>
            </ul>
            <div className="cta-buttons">
              {user ? (
                <a href="/dashboard" className="btn btn-outline">
                  Dashboard
                </a>
              ) : (
                <a href="/auth" className="btn btn-outline">
                  Get Started
                </a>
              )}
            </div>
            <div className="mobile-menu-toggle" onClick={toggleMobileMenu}>
              <span className={`menu-icon ${mobileMenuOpen ? 'active' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </div>
          </div>
        </div>
      </header>

      <section className="hero">
        <div className="container">
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="hero-title">
              Find Your Perfect Home with AI Intelligence
            </h1>
            <p className="hero-subtitle">
              Discover, schedule viewings, and make offers on properties — all
              powered by advanced AI that understands your unique preferences. Save time and find exactly what you're looking for.
            </p>
            <div className="hero-stats">
              <div className="stat-item">
                <span className="stat-number">93%</span>
                <span className="stat-label">Faster Search</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">5,000+</span>
                <span className="stat-label">Happy Users</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">98%</span>
                <span className="stat-label">Satisfaction</span>
              </div>
            </div>
            <div className="hero-cta">
              <a href="/auth" className="btn btn-primary pulse-animation">
                Start Your AI Home Search
              </a>
              <a href="#how-it-works" className="btn btn-outline">
                See How It Works
              </a>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="hero-image-container"
          >
            <LazyLoadImage
              src="/api/placeholder/600/450"
              alt="AI-powered real estate discovery"
              className="hero-image"
              effect="blur"
              placeholderSrc="/api/placeholder/60/45"
            />
          </motion.div>
        </div>
      </section>

      <section className="trust-banner">
        <div className="container">
          <div className="partners-row">
            <p className="partners-label">Trusted by leading real estate partners:</p>
            <div className="partner-logos">
              {partners.map((partner, index) => (
                <img key={index} src={partner.logo} alt={partner.name} className="partner-logo" />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="features section" id="features" ref={featuresRef}>
        <div className="container">
          <motion.div
            variants={fadeIn}
            initial="hidden"
            animate={featuresVisible ? "visible" : "hidden"}
          >
            <h2 className="section-title">Intelligent Real Estate Solutions</h2>
            <p className="section-subtitle">
              Our AI-powered platform revolutionizes the property search process
            </p>

            <div className="features-grid">
              <div className="feature-card">
                <div className="feature-icon">🔍</div>
                <h3 className="feature-title">Smart Discovery</h3>
                <p>
                  Our AI learns your preferences and recommends properties that
                  truly match what you're looking for, saving you hours of
                  searching.
                </p>
                <a href="#" className="feature-link">Learn more →</a>
              </div>

              <div className="feature-card">
                <div className="feature-icon">📅</div>
                <h3 className="feature-title">Easy Scheduling</h3>
                <p>
                  Schedule property viewings with a single click. Our platform
                  coordinates with agents and owners automatically.
                </p>
                <a href="#" className="feature-link">Learn more →</a>
              </div>

              <div className="feature-card">
                <div className="feature-icon">📝</div>
                <h3 className="feature-title">Smart Offers</h3>
                <p>
                  Make data-driven offers with AI assistance that analyzes market
                  trends and property value to help you submit competitive bids.
                </p>
                <a href="#" className="feature-link">Learn more →</a>
              </div>

              <div className="feature-card">
                <div className="feature-icon">📊</div>
                <h3 className="feature-title">Market Insights</h3>
                <p>
                  Get real-time neighborhood analytics, price trends, and
                  investment potential for informed decision-making.
                </p>
                <a href="#" className="feature-link">Learn more →</a>
              </div>

              <div className="feature-card">
                <div className="feature-icon">💬</div>
                <h3 className="feature-title">AI Assistant</h3>
                <p>
                  Ask questions about any property or neighborhood and get
                  instant, accurate answers from our intelligent assistant.
                </p>
                <a href="#" className="feature-link">Learn more →</a>
              </div>

              <div className="feature-card">
                <div className="feature-icon">🔒</div>
                <h3 className="feature-title">Secure Transactions</h3>
                <p>
                  Complete transactions securely through our platform with
                  transparent documentation and verification.
                </p>
                <a href="#" className="feature-link">Learn more →</a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="how-it-works section" id="how-it-works" ref={howItWorksRef}>
        <div className="container">
          <motion.div
            variants={fadeIn}
            initial="hidden"
            animate={howItWorksVisible ? "visible" : "hidden"}
          >
            <h2 className="section-title">How Thuis.AI Works</h2>
            <p className="section-subtitle">
              Finding your perfect home has never been this simple
            </p>

            <div className="steps">
              <div className="step">
                <div className="step-number">1</div>
                <h3 className="step-title">Create Profile</h3>
                <p>Tell us about your preferences, budget, and must-haves.</p>
              </div>

              <div className="step">
                <div className="step-number">2</div>
                <h3 className="step-title">Discover</h3>
                <p>Browse AI-curated listings tailored to your needs.</p>
              </div>

              <div className="step">
                <div className="step-number">3</div>
                <h3 className="step-title">Schedule</h3>
                <p>Book viewings with a single click.</p>
              </div>

              <div className="step">
                <div className="step-number">4</div>
                <h3 className="step-title">Make Offer</h3>
                <p>Submit offers with AI-powered market insights.</p>
              </div>

              <div className="step">
                <div className="step-number">5</div>
                <h3 className="step-title">Complete</h3>
                <p>Finalize your purchase with guided assistance.</p>
              </div>
            </div>

            <div className="demo-cta">
              <a href="#" className="btn btn-secondary">Watch Demo</a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing component */}
      <Pricing />

      {/* New Testimonials Section */}
      <section className="testimonials section" id="testimonials" ref={testimonialsRef}>
        <div className="container">
          <motion.div
            variants={fadeIn}
            initial="hidden"
            animate={testimonialsVisible ? "visible" : "hidden"}
          >
            <h2 className="section-title">What Our Users Say</h2>
            <p className="section-subtitle">
              Join thousands of satisfied users who found their dream homes with thuis.ai
            </p>

            <div className="testimonials-wrapper">
              {testimonials.map((testimonial, index) => (
                <div className="testimonial" key={index}>
                  <div className="testimonial-stars">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className={i < testimonial.rating ? "star active" : "star"}>★</span>
                    ))}
                  </div>
                  <p className="testimonial-text">"{testimonial.quote}"</p>
                  <div className="testimonial-author">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.name} 
                      className="testimonial-avatar" 
                    />
                    <div className="testimonial-info">
                      <h4>{testimonial.name}</h4>
                      <span className="testimonial-role">{testimonial.title}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq section" id="faq">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-grid">
            {faqs.map((faq, index) => (
              <div className="faq-item" key={index}>
                <h3 className="faq-question">{faq.question}</h3>
                <p className="faq-answer">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <BlogList />

      {/* Security Badges Section */}
      <section className="security-section">
        <div className="container">
          <div className="security-badges">
            <div className="security-badge">
              <img src="/api/placeholder/80/80" alt="Data Protection" className="badge-icon" />
              <h4>Data Protection</h4>
              <p>Your personal data is protected with bank-level encryption</p>
            </div>
            <div className="security-badge">
              <img src="/api/placeholder/80/80" alt="Secure Payments" className="badge-icon" />
              <h4>Secure Payments</h4>
              <p>All transactions are protected with SSL encryption</p>
            </div>
            <div className="security-badge">
              <img src="/api/placeholder/80/80" alt="Privacy Guarantee" className="badge-icon" />
              <h4>Privacy Guarantee</h4>
              <p>We never share your information without your consent</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cta section">
        <div className="container">
          <h2 className="section-title">Ready to Find Your Dream Home?</h2>
          <p className="section-subtitle">
            Join thousands of satisfied home seekers using Thuis.AI.
          </p>
          <div className="cta-buttons">
            <a href="/auth" className="btn btn-secondary pulse-animation">
              Start Your Free Trial
            </a>
            <a href="#" className="btn btn-outline">
              Contact Us
            </a>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div>
              <div className="footer-logo">
                thuis<span className="logo-dot">.ai</span>
              </div>
              <p className="footer-description">
                AI-powered real estate platform for discovering, scheduling
                viewings, and making offers on properties.
              </p>
              <div className="social-links">
                <a href="#" className="social-link">
                  🐦
                </a>
                <a href="#" className="social-link">
                  📘
                </a>
                <a href="#" className="social-link">
                  📸
                </a>
              </div>
            </div>
            <div>
              <h4 className="footer-heading">Company</h4>
              <ul className="footer-links">
                <li>
                  <a href="#" className="footer-link">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="#" className="footer-link">
                    Careers
                  </a>
                </li>
                <li>
                  <a href="#" className="footer-link">
                    Contact
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="footer-heading">Support</h4>
              <ul className="footer-links">
                <li>
                  <a href="#" className="footer-link">
                    Help Center
                  </a>
                </li>
                <li>
                  <a href="#" className="footer-link">
                    FAQs
                  </a>
                </li>
                <li>
                  <a href="#" className="footer-link">
                    Report an Issue
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="footer-heading">Legal</h4>
              <ul className="footer-links">
                <li>
                  <a href="#" className="footer-link">
                    Terms of Service
                  </a>
                </li>
                <li>
                  <a href="#" className="footer-link">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="#" className="footer-link">
                    Cookies Policy
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            &copy; 2025 Thuis.AI. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  )
}

export default LandingPage